import React, { useEffect, useState } from 'react';
import { Plus, BookOpen } from 'lucide-react';
import { toast } from 'react-toastify';
import useOrganizationMemberActivityStore from '@/stores/AdminStore/OrganizationMemberActivityStore';
import { LoadingSpinner } from '@/Components/common';
import EnrollCourseModal from '../modals/EnrollCourseModal';

interface Props { userId: string; }

const formatDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');

const CoursesTab: React.FC<Props> = ({ userId }) => {
  const {
    courses, loadingCourses, enrolling,
    fetchCourses, fetchEnrollable, enrollInCourse
  } = useOrganizationMemberActivityStore();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => { fetchCourses(userId); }, [userId, fetchCourses]);

  const handleEnroll = async (courseId: number) => {
    const res = await enrollInCourse(userId, courseId);
    if (res.success) {
      toast.success(res.message);
      setShowModal(false);
    } else {
      toast.error(res.message);
    }
  };

  return (
    <div>
      <div className="omact-toolbar">
        <h3 style={{ margin: 0, fontSize: 16 }}>Enrolled courses ({courses.length})</h3>
        <button className="omact-btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={16} /> Enroll in course
        </button>
      </div>

      {loadingCourses ? (
        <div style={{ textAlign: 'center', padding: 32 }}><LoadingSpinner size="md" /></div>
      ) : courses.length === 0 ? (
        <div className="omact-empty">
          <BookOpen size={40} color="#9ca3af" />
          <p>No course enrolments yet.</p>
        </div>
      ) : (
        <table className="omact-table">
          <thead>
            <tr>
              <th>Course</th>
              <th>Status</th>
              <th>Progress</th>
              <th>Enrolled</th>
              <th>Completed</th>
              <th>Last activity</th>
            </tr>
          </thead>
          <tbody>
            {courses.map((c) => (
              <tr key={c.course_id}>
                <td>{c.title}</td>
                <td><span className={`omact-badge omact-badge-${c.status}`}>{c.status.replace('_', ' ')}</span></td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div className="omact-progress-bar">
                      <div className="omact-progress-fill" style={{ width: `${Math.min(100, Math.max(0, c.progress))}%` }} />
                    </div>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>{Math.round(c.progress)}%</span>
                  </div>
                </td>
                <td>{formatDate(c.enrolled_date)}</td>
                <td>{formatDate(c.completed_at)}</td>
                <td>{formatDate(c.last_activity)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && (
        <EnrollCourseModal
          userId={userId}
          enrolling={enrolling}
          fetchEnrollable={fetchEnrollable}
          onConfirm={handleEnroll}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

export default CoursesTab;
